import { RRule, Frequency, Weekday } from 'rrule';
import { EventTime } from '../event.time';
import { FrequencyKind, RecurrenceRule } from '../recurrence.rule';
import { OccurrenceGenerator } from './occurrence.generator';

export class RRuleGenerator implements OccurrenceGenerator {
  private static readonly KIND_TO_FREQ: Partial<Record<FrequencyKind, Frequency>> = {
    daily: RRule.DAILY,
    weekly: RRule.WEEKLY,
    biweekly: RRule.WEEKLY,
    monthly: RRule.MONTHLY,
    last: RRule.MONTHLY,
  };

  generate(rule: RecurrenceRule): EventTime[] {
    const freq = RRuleGenerator.KIND_TO_FREQ[rule.kind];
    if (freq === undefined) {
      return [];
    }

    const startWall = wallClockUTC(rule.range.startDateTime, rule.timezone);
    const endWall = wallClockUTC(rule.range.endDateTime, rule.timezone);

    // Use start time-of-day on the end date as the cutoff — if start > end time-of-day (e.g. 10 PM start, 2 AM end),
    // using endWall directly would exclude the last occurrence since 10 PM wall > 2 AM until.
    const untilWall = new Date(Date.UTC(
      endWall.getUTCFullYear(), endWall.getUTCMonth(), endWall.getUTCDate(),
      startWall.getUTCHours(), startWall.getUTCMinutes(), startWall.getUTCSeconds(),
    ));

    const options: ConstructorParameters<typeof RRule>[0] = {
      freq,
      dtstart: startWall,
      until: untilWall,
      interval: rule.kind === 'biweekly' ? 2 : 1,
    };

    if (rule.kind === 'weekly' || rule.kind === 'biweekly') {
      options.byweekday = [dayOfWeekToWeekday(getDayOfWeek(rule.range.startDateTime, rule.timezone))];
    } else if (rule.kind === 'monthly') {
      const dow = getDayOfWeek(rule.range.startDateTime, rule.timezone);
      const setpos = getNthWeekdayOfMonth(rule.range.startDateTime, rule.timezone);
      options.byweekday = [dayOfWeekToWeekday(dow)];
      options.bysetpos = [setpos];
    } else if (rule.kind === 'last') {
      const dow = getDayOfWeek(rule.range.startDateTime, rule.timezone);
      options.byweekday = [dayOfWeekToWeekday(dow)];
      options.bysetpos = [-1];
    }

    const rrule = new RRule(options);
    const wallOccurrences = rrule.all();

    return wallOccurrences.map(wall => {
      const realStartMs = wallUTCToReal(wall, rule.timezone);
      const occEndWall = new Date(Date.UTC(
        wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate(),
        endWall.getUTCHours(), endWall.getUTCMinutes(), endWall.getUTCSeconds(),
      ));
      let realEndMs = wallUTCToReal(occEndWall, rule.timezone);
      if (!rule.isAllDay && realEndMs <= realStartMs) realEndMs += MS_PER_DAY;
      return {
        startTime: new Date(realStartMs).toISOString(),
        endTime: new Date(realEndMs).toISOString(),
        isAllDay: rule.isAllDay,
        isEndAllDay: rule.isEndAllDay,
      };
    });
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function wallClockUTC(iso: string, timezone: string): Date {
  // Take an ISO string and return a Date whose UTC wall-clock components match the local
  // wall-clock in `timezone`. rrule expects this "shape".
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date(iso)).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return new Date(Date.UTC(
    +parts['year'],
    +parts['month'] - 1,
    +parts['day'],
    +parts['hour'] === 24 ? 0 : +parts['hour'],
    +parts['minute'],
    +parts['second'],
  ));
}

function wallUTCToReal(wall: Date, timezone: string): number {
  const target = wall.getTime();
  let guess = target;
  let prev = NaN;
  for (let i = 0; i < 6; i++) {
    const offsetMs = getTimezoneOffsetMs(new Date(guess), timezone);
    const next = target - offsetMs;
    if (next === guess) return guess;          // converged
    if (next === prev) return Math.max(guess, prev); // DST gap — snap to post-gap instant
    prev = guess;
    guess = next;
  }
  return guess;
}

function getTimezoneOffsetMs(at: Date, timezone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(at).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  const localAsUTC = Date.UTC(
    +parts['year'],
    +parts['month'] - 1,
    +parts['day'],
    +parts['hour'] === 24 ? 0 : +parts['hour'],
    +parts['minute'],
    +parts['second'],
  );
  return localAsUTC - at.getTime();
}

function getDayOfWeek(iso: string, timezone: string): number {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' });
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[f.format(new Date(iso))] ?? 0;
}

function getNthWeekdayOfMonth(iso: string, timezone: string): number {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: timezone, day: 'numeric' });
  const dayOfMonth = +f.format(new Date(iso));
  return Math.ceil(dayOfMonth / 7);
}

function dayOfWeekToWeekday(dow: number): Weekday {
  // rrule weekdays: MO=0, TU=1, ... SU=6. Intl weekdays: SU=0, MO=1, ..., SA=6.
  const map = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
  return map[dow];
}
